import { css } from "@emotion/react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import type { FixedLengthArray } from "type-fest";

const cellStyles = css`
  flex: 1 1 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:not(:first-of-type) {
    text-align: right;
  }

  :first-of-type {
    flex-grow: 2;
  }

  :last-of-type {
    padding-left: 25px;
  }
`;

const cellHeaderStyles = css`
  &:not(:first-of-type) {
    font-weight: 300;
  }

  :first-of-type {
    font-weight: bold;
  }
`;

export const SimpleTableSection = (
  props: ComponentPropsWithoutRef<"div"> & {
    hasStartGutter?: boolean;
  },
) => {
  const { hasStartGutter, ...others } = props;

  return (
    <div
      css={[
        css`
          padding-block-end: calc(2.5 * var(--report-root-font-size));
          page-break-inside: avoid;
        `,
        hasStartGutter &&
          css`
            padding-block-start: calc(2.5 * var(--report-root-font-size));
          `,
      ]}
      {...others}
    />
  );
};

export const SimpleTableRow = <TCellCount extends number = 6>(props: {
  isHeader?: boolean;
  bold?: boolean;
  borderBottom?: boolean;
  borderTop?: boolean;
  thickerBorder?: boolean;
  /**
   * Override the default cell count for the rare situation where it needs to
   * be different from six.
   *
   * This has no effect at runtime but statically enforces the size of the cells
   * array to help maintaining the different sections consistency.
   */
  cellCount?: TCellCount;
  cells: FixedLengthArray<ReactNode, NoInfer<TCellCount>>;
}) => {
  const {
    bold,
    borderBottom,
    borderTop,
    cells,
    isHeader,
    thickerBorder,
    // omit cell count since it has no runtime effect
    cellCount: _,
    ...others
  } = props;

  return (
    <div
      css={[
        css`
          display: flex;
          align-items: center;
          font-family: "Lato", sans-serif;
          padding: 2px 0;
        `,
        bold &&
          css`
            font-weight: bold;
          `,
        borderBottom &&
          css`
            border-bottom: ${thickerBorder ? 2 : 1}px solid rgba(0, 0, 0, 0.5);
          `,
        borderTop &&
          css`
            border-top: ${thickerBorder ? 2 : 1}px solid rgba(0, 0, 0, 0.5);
          `,
        isHeader &&
          css`
            border-bottom: 2px solid rgba(0, 0, 0, 0.5);
          `,
      ]}
      {...others}
    >
      {cells.map((cell, i) => (
        <div key={i} css={[cellStyles, isHeader && cellHeaderStyles]}>
          {cell}
        </div>
      ))}
    </div>
  );
};
